import type { VoidFunctionComponent } from 'react';
import { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import type { FossaPlanName, FossaSubscriptionWithPlanName } from '../../../shared/subscriptions';
import { fossaPlanMap, FREE_PLAN_NAME } from '../../../shared/subscriptions';
import { window } from '../../globals';
import SubscriptionStore from '../../redux/subscriptions';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../generic/Button';
import Flex from '../generic/Flex';
import Loader from '../generic/Loader';
import SubscriptionPath from '../settings/billing/SubscriptionPath';
import SecurityRequired from '../generic/Gates/SecurityRequired';

type ModalDowngradeSubscriptionProps = {
  isShowing: boolean;
  onHide: () => void;
  orgCurrPlan: FossaPlanName;
};

const freePlanDetailsWithName: FossaSubscriptionWithPlanName = {
  planName: FREE_PLAN_NAME as FossaPlanName,
  ...fossaPlanMap[FREE_PLAN_NAME],
};

const ModalDowngradeSubscription: VoidFunctionComponent<
  ModalDowngradeSubscriptionProps
> = ({ isShowing, onHide, orgCurrPlan }) => {
  const dispatch = useDispatch();
  const wasCanceled = useSelector(SubscriptionStore.selectors.wasCanceled);
  const status = useSelector(SubscriptionStore.selectors.getLoading);

  useEffect(() => {
    if (wasCanceled) {
      window.location.reload();
    }
  }, [wasCanceled]);

  const currPlanDetailsWithName: FossaSubscriptionWithPlanName = {
    planName: orgCurrPlan,
    ...fossaPlanMap[orgCurrPlan],
  };
  return (
    <Modal show={isShowing} onHide={onHide} className="downgrade-modal">
      <Modal.Header>Warning: Downgrade Effects</Modal.Header>
      {status.loading || wasCanceled ? (
        <Modal.Body>
          <Loader />
        </Modal.Body>
      ) : (
        <>
          <Modal.Body>
            You are about to downgrade you current plan from...
            <span className="plan-change-details">
              <SubscriptionPath
                oldPlan={currPlanDetailsWithName}
                newPlan={freePlanDetailsWithName}
                hidePrices
              />
            </span>
            This action will have the following effects on your account:
            <ul className="downgrade-effects">
              <li>Remove all projects beyond the most recent 5.</li>
              <li>Limit dependency scan depth to 5 levels.</li>
              <SecurityRequired>
                <li>Remove security vulnerability scanning functionality.</li>
              </SecurityRequired>
            </ul>
            <div className="confirmation-message">
              Are you sure you want to downgrade your account?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Flex justify="right" gap="10px">
              <Button
                size={BUTTON_SIZE.MEDIUM}
                type={BUTTON_TYPE.SECONDARY}
                onClick={onHide}
              >
                Cancel
              </Button>
              <Button
                size={BUTTON_SIZE.MEDIUM}
                type={BUTTON_TYPE.ERROR}
                onClick={() => {
                  dispatch(SubscriptionStore.actions.cancelSubscription());
                }}
              >
                Downgrade
              </Button>
            </Flex>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ModalDowngradeSubscription;
